import React, { useEffect, useMemo, useState } from "react";
import "../orderList/orderList.css";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { userRequest, publicRequest } from "../../requestMethods";
import moment from "moment";
import { FiPlus, FiEdit, FiTrash2, FiEye, FiCheckCircle, FiXCircle, FiDownload, FiPackage } from "react-icons/fi";
import { Close, CloudDownload } from "@material-ui/icons";
import Select from "react-select";
import { commonSelectProps } from "../../components/shared/SelectStyles";
import createPurchaseOrder from "../../components/pdf/createPurchaseOrder";

const statusOptions = [
    { label: "All", value: "" },
    { label: "Draft", value: "draft" },
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
    { label: "Completed", value: "completed" },
];

const PurchaseOrders = () => {
    const location = useLocation();
    const currentUser = useSelector((state) => state.user.currentUser);
    const [purchaseOrders, setPurchaseOrders] = useState({ payload: [], totalPages: 0 });
    const [loading, setLoading] = useState(true);
    const [companiesData, setCompaniesData] = useState([]);
    const [selectedPO, setSelectedPO] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [completeMenu, setCompleteMenu] = useState(false);
    const [rejectMenu, setRejectMenu] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [rejectRemarks, setRejectRemarks] = useState("");
    const [sidebarWidth, setSidebarWidth] = useState(280);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editForm, setEditForm] = useState(null);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [materialsData, setMaterialsData] = useState([]);

    const materialsMapping = useMemo(() => {
        const mapping = {};
        (materialsData || []).forEach((material) => {
            const normalizedTitle = (material?.title || "").trim().toLowerCase();
            const isExcluded =
                normalizedTitle === "others" ||
                normalizedTitle === "other" ||
                normalizedTitle === "add on" ||
                normalizedTitle === "add-on" ||
                normalizedTitle === "addon";
            if (!isExcluded && material?.itemCode) {
                mapping[material.itemCode] = material.title;
            }
        });
        return mapping;
    }, [materialsData]);

    const itemCodeOptions = useMemo(
        () =>
            Object.entries(materialsMapping).map(([code, title]) => ({
                label: `${code} - ${title}`,
                value: code,
            })),
        [materialsMapping]
    );

    const superadmin = currentUser?.isSuperAdmin;
    const outright = currentUser?.type === "outright";
    const manager = currentUser?.type === "manager";
    const purchaser = currentUser?.type === "purchaser";
    const productionManager = currentUser?.type === "productionManager";

    const canCreate = superadmin || manager || outright || purchaser || (productionManager && process.env.REACT_APP_PROJECT === "JINSORA");

    // Determine principle parameter based on user type
    const getPrincipleParam = () => {
        if (superadmin) {
            // SuperAdmin can see all purchase orders
            return undefined;
        } else if (outright || manager || (productionManager && process.env.REACT_APP_PROJECT === "JINSORA")) {
            // Outright, manager, and productionManager (for JINSORA) users can only see their own principle
            return currentUser?._id;
        } else {
            // For other user types, use their principle if available
            return currentUser?.principle;
        }
    };

    const [filters, setFilters] = useState(() => {
        const params = new URLSearchParams(location.search);
        const poNumberFromUrl = params.get("poNumber") || "";
        return {
            status: "pending",
            company: "",
            poNumber: poNumberFromUrl,
            classificationCode: "",
            dateFrom: moment("2026-01-01").format("YYYY-MM-DD"),
            dateTo: moment(Date.now()).add(1, "months").format("YYYY-MM-DD"),
            limit: 20,
            page: 1,
            principle: getPrincipleParam(),
        };
    });

    useEffect(() => {
        // Update principle in filters when currentUser changes
        const principleParam = getPrincipleParam();
        setFilters((prev) => {
            // Only update if principle actually changed to avoid unnecessary re-renders
            if (prev.principle !== principleParam) {
                return {
                    ...prev,
                    principle: principleParam,
                };
            }
            return prev;
        });
    }, [currentUser?._id, currentUser?.principle, currentUser?.type, superadmin]);

    // Sync poNumber from URL when it changes (e.g. browser back/forward)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const poNumberFromUrl = params.get("poNumber") || "";
        setFilters((prev) => {
            if (prev.poNumber !== poNumberFromUrl) {
                return { ...prev, poNumber: poNumberFromUrl, page: 1 };
            }
            return prev;
        });
    }, [location.search]);

    useEffect(() => {
        fetchPurchaseOrders();
    }, [filters]);

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        const fetchMaterialsData = async () => {
            try {
                const params = {};
                if (currentUser) {
                    if (currentUser.isSuperAdmin) {
                        // Superadmin can see all materials - no filter
                    } else if (currentUser.type === "outright") {
                        params.principle = currentUser._id;
                    } else if (currentUser.principle) {
                        params.principle = currentUser.principle;
                    }
                }
                const res = await publicRequest.get("/materials/", { params });
                setMaterialsData(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                console.error("Error fetching materials:", err);
            }
        };
        fetchMaterialsData();
    }, [currentUser?._id, currentUser?.principle, currentUser?.type, currentUser?.isSuperAdmin]);

    useEffect(() => {
        const updateSidebarWidth = () => {
            const sidebar = document.querySelector('.sidebar');
            if (sidebar) {
                const isCollapsed = sidebar.classList.contains('collapsed');
                setSidebarWidth(isCollapsed ? 80 : 280);
            } else {
                setSidebarWidth(280);
            }
        };

        updateSidebarWidth();

        // Watch for sidebar collapse/expand
        const sidebar = document.querySelector('.sidebar');
        if (sidebar) {
            const observer = new MutationObserver(updateSidebarWidth);
            observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
            
            window.addEventListener('resize', updateSidebarWidth);
            
            return () => {
                observer.disconnect();
                window.removeEventListener('resize', updateSidebarWidth);
            };
        }
    }, []);

    useEffect(() => {
        if (showModal === true) {
            document.body.style.overflow = "hidden";
            window.scrollTo(0, 0);
        } else {
            document.body.style.overflow = "inherit";
        }
    }, [showModal]);

    useEffect(() => {
        if (completeMenu === true) {
            document.body.style.overflow = "hidden";
            window.scrollTo(0, 0);
        } else {
            document.body.style.overflow = "inherit";
        }
    }, [completeMenu]);

    useEffect(() => {
        if (rejectMenu === true) {
            document.body.style.overflow = "hidden";
            window.scrollTo(0, 0);
        } else {
            document.body.style.overflow = "inherit";
        }
    }, [rejectMenu]);

    const fetchPurchaseOrders = async () => {
        try {
            setLoading(true);
            const res = await userRequest.get("/purchaseOrders", {
                params: {
                    filters: JSON.stringify(filters)
                }
            });
            setPurchaseOrders(res.data);
        } catch (err) {
            console.error("Error fetching purchase orders:", err);
            alert("Failed to fetch purchase orders");
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanies = async () => {
        try {
            const filterData = {
                status: "active",
                limit: 9999,
                page: 1,
            };
            const res = await publicRequest.get("/companies", {
                params: {
                    filters: JSON.stringify(filterData)
                }
            });
            setCompaniesData(res.data?.companies || res.data || []);
        } catch (err) {
            console.error("Error fetching companies:", err);
        }
    };

    const handleResetFilters = () => {
        const principleParam = getPrincipleParam();
        setFilters({
            status: "",
            company: "",
            poNumber: "",
            classificationCode: "",
            dateFrom: moment().subtract(6, 'months').format("YYYY-MM-DD"),
            dateTo: moment().format("YYYY-MM-DD"),
            limit: 20,
            page: 1,
            principle: principleParam,
        });
    };

    const openModal = (po) => {
        setSelectedPO(po);
        setIsEditMode(false);
        setEditForm(null);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedPO(null);
        setIsEditMode(false);
        setEditForm(null);
    };

    const isPOEditable = (po) => po && po.status !== "completed" && po.status !== "rejected";

    const buildEditFormFromPO = (po) => ({
        dateOfIssue: po.dateOfIssue ? moment(po.dateOfIssue).format("YYYY-MM-DD") : "",
        company:
            po.company?._id != null
                ? String(po.company._id)
                : po.company != null && po.company !== ""
                  ? String(po.company)
                  : "",
        remarks: po.remarks || "",
        materials: (po.materials || []).map((m) => ({
            productId: m.productId || m._id || null,
            code: m.code || "",
            img: m.img || "",
            type: m.type || "",
            itemCode: m.itemCode || "",
            model: m.model || "",
            price: m.price ?? 0,
            quantity: m.quantity ?? m.amount ?? 0,
            classificationCode: m.classificationCode || "",
            company: m.company?._id || m.company || "",
            measurements: m.measurements || "",
        })),
    });

    const openEditModal = (po) => {
        if (!isPOEditable(po)) {
            alert("Completed or rejected purchase orders cannot be edited.");
            return;
        }
        setSelectedPO(po);
        setEditForm(buildEditFormFromPO(po));
        setIsEditMode(true);
        setShowModal(true);
    };

    const startEditFromModal = () => {
        if (!selectedPO) return;
        if (!isPOEditable(selectedPO)) {
            alert("Completed or rejected purchase orders cannot be edited.");
            return;
        }
        setEditForm(buildEditFormFromPO(selectedPO));
        setIsEditMode(true);
    };

    const cancelEdit = () => {
        setIsEditMode(false);
        setEditForm(null);
    };

    const updateEditField = (field, value) => {
        setEditForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

    const updateMaterialField = (index, field, value) => {
        setEditForm((prev) => {
            if (!prev) return prev;
            const materials = [...prev.materials];
            materials[index] = { ...materials[index], [field]: value };
            return { ...prev, materials };
        });
    };

    const updateMaterialFields = (index, partial) => {
        setEditForm((prev) => {
            if (!prev) return prev;
            const materials = [...prev.materials];
            materials[index] = { ...materials[index], ...partial };
            return { ...prev, materials };
        });
    };

    const removeMaterialRow = (index) => {
        setEditForm((prev) => {
            if (!prev) return prev;
            const materials = prev.materials.filter((_, i) => i !== index);
            return { ...prev, materials };
        });
    };

    const addBlankAddOnRow = () => {
        setEditForm((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                materials: [
                    ...prev.materials,
                    {
                        productId: null,
                        code: "",
                        img: "",
                        type: "",
                        itemCode: "",
                        model: "",
                        price: 0,
                        quantity: 1,
                        classificationCode: selectedPO?.classificationCode || "",
                        company: prev.company || "",
                        measurements: "",
                    },
                ],
            };
        });
    };

    const handleSaveEdit = async () => {
        if (!editForm || !selectedPO) return;

        if (!editForm.dateOfIssue) {
            alert("Date of Issue is required.");
            return;
        }
        if (!editForm.company) {
            alert("Company is required.");
            return;
        }

        setIsSavingEdit(true);
        try {
            const payload = {
                dateOfIssue: editForm.dateOfIssue,
                company: editForm.company,
                remarks: editForm.remarks || "",
                materials: editForm.materials.map((m) => {
                    const price = Number(m.price) || 0;
                    const quantity = Number(m.quantity) || 0;
                    return {
                        ...(m.productId ? { productId: m.productId } : {}),
                        code: m.code || "",
                        img: m.img || "",
                        type: m.type || "",
                        itemCode: m.itemCode || "",
                        model: m.model || m.type || "",
                        price,
                        quantity,
                        amount: quantity,
                        classificationCode: m.classificationCode || "",
                        company: m.company || "",
                        measurements: m.measurements || "",
                    };
                }),
            };

            const res = await userRequest.put(
                `/purchaseOrders/${selectedPO._id}`,
                payload
            );

            if (res.data) {
                alert("Purchase order updated successfully.");
                setSelectedPO(res.data);
                setIsEditMode(false);
                setEditForm(null);
                fetchPurchaseOrders();
            }
        } catch (err) {
            console.error("Error updating purchase order:", err);
            if (err.response?.status === 409) {
                alert(
                    err.response?.data?.message ||
                        "PO Number already exists. Please use a different number."
                );
            } else if (err.response?.data?.message) {
                alert(err.response.data.message);
            } else {
                alert("Failed to update purchase order. Please try again.");
            }
        } finally {
            setIsSavingEdit(false);
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            draft: "bg-gray-400",
            pending: "bg-yellow-400",
            approved: "bg-green-400",
            rejected: "bg-red-400",
            completed: "bg-blue-400",
        };
        return (
            <span className={`px-3 py-1 rounded-full text-white text-xs font-semibold ${colors[status] || 'bg-gray-400'}`}>
                {status?.toUpperCase()}
            </span>
        );
    };

    const formatCurrency = (amount) => {
        return `RM ${parseFloat(amount || 0).toFixed(2)}`;
    };

    const handleDownloadPurchaseOrder = async (po) => {
        try {
            // Transform purchase order data to match createPurchaseOrder format
            const orderData = {
                invoiceId: po.poNumber?.replace('PO-', '') || po.poNumber || 'N/A',
                coNumber: po.classificationCode || '',
                createdAt: po.dateOfIssue || po.createdAt,
                userId: {
                    name: po.company?.name || 'N/A',
                    phoneNumber: po.company?.phoneNumber || 'N/A'
                },
                lineOne: po.company?.address?.lineOne || '',
                lineTwo: po.company?.address?.lineTwo || '',
                zip: po.company?.address?.zip || '',
                city: po.company?.address?.city || '',
                country: po.company?.address?.country || '',
                materials: po.materials?.map(material => ({
                    name: material.type || material.model || 'N/A',
                    type: material.type || material.model || 'N/A',
                    code: material.itemCode || material.code || 'N/A',
                    measurements: material.measurements || '',
                    amount: material.quantity || material.amount || 0,
                    quantity: material.quantity || material.amount || 0,
                    price: material.price || 0,
                    activeColor: {
                        type: material.type || material.model || 'N/A',
                        itemCode: material.itemCode || material.code || 'N/A',
                        price: material.price || 0
                    }
                })) || []
            };
            
            await createPurchaseOrder(orderData, { saveAs: true });
        } catch (error) {
            console.error("Error downloading purchase order:", error);
            alert("Failed to download purchase order. Please try again.");
        }
    };

    const handleCompletePO = (po, e) => {
        e.stopPropagation();
        
        if (po.status !== 'pending' && po.status !== 'approved') {
            alert('Only pending or approved purchase orders can be marked as completed.');
            return;
        }

        setModalData(po);
        setCompleteMenu(true);
    };

    const confirmCompletePO = async () => {
        try {
            await userRequest.put(`/purchaseOrders/updateStatus/${modalData._id}`, {
                status: 'completed'
            });
            setCompleteMenu(false);
            setModalData(null);
            fetchPurchaseOrders();
        } catch (err) {
            console.error("Error completing purchase order:", err);
            alert('Failed to complete purchase order. Please try again.');
        }
    };

    const handleRejectPO = (po, e) => {
        e.stopPropagation();
        
        if (po.status === 'completed' || po.status === 'rejected') {
            alert('This purchase order cannot be rejected.');
            return;
        }

        setModalData(po);
        setRejectRemarks("");
        setRejectMenu(true);
    };

    const confirmRejectPO = async () => {
        try {
            await userRequest.put(`/purchaseOrders/updateStatus/${modalData._id}`, {
                status: 'rejected',
                remarks: rejectRemarks
            });
            setRejectMenu(false);
            setModalData(null);
            setRejectRemarks("");
            fetchPurchaseOrders();
        } catch (err) {
            console.error("Error rejecting purchase order:", err);
            alert('Failed to reject purchase order. Please try again.');
        }
    };

    if (!canCreate) {
        return (
            <div className="container mx-auto px-2 sm:px-4 py-8 block">
                <div className="bg-white rounded-lg shadow p-8 text-center">
                    <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
                    <p className="text-gray-700">Only Superadmin, Manager, Outright, and Purchaser users can access purchase orders.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-2 sm:px-4 py-8 block">
            {/* Filter Panel */}
            <div className="bg-white rounded-lg shadow p-2 sm:p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
                        <input
                            type="date"
                            value={filters.dateFrom}
                            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value, page: 1 })}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
                        <input
                            type="date"
                            value={filters.dateTo}
                            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value, page: 1 })}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <Select
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === filters.status)}
                            onChange={(selected) => setFilters({ ...filters, status: selected.value, page: 1 })}
                            placeholder="Select Status"
                            {...commonSelectProps}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                        <Select
                            options={companiesData.map(c => ({ label: c.name, value: c._id }))}
                            value={companiesData.find(c => c._id === filters.company) ? {
                                label: companiesData.find(c => c._id === filters.company).name,
                                value: filters.company
                            } : null}
                            onChange={(selected) => setFilters({ ...filters, company: selected?.value || "", page: 1 })}
                            placeholder="Select Company"
                            isClearable
                            {...commonSelectProps}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                        <input
                            type="text"
                            value={filters.poNumber}
                            onChange={(e) => setFilters({ ...filters, poNumber: e.target.value, page: 1 })}
                            placeholder="Search PO Number"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Classification Code</label>
                        <input
                            type="text"
                            value={filters.classificationCode}
                            onChange={(e) => setFilters({ ...filters, classificationCode: e.target.value, page: 1 })}
                            placeholder="Search Classification"
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>
                </div>

                <div className="flex justify-start mt-4">
                    <button
                        onClick={handleResetFilters}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded"
                    >
                        Reset Filters
                    </button>
                </div>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow overflow-x-auto orders-table-wrapper">
                <table className="min-w-[1200px] divide-y divide-gray-200 orders-table w-full">
                    <thead className="bg-orange-400">
                        <tr>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">PO Number</th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">Date of Issue</th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">Company</th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">Classification</th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">Total Amount</th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">Status</th>
                            <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-[10px] sm:text-xs font-medium text-black uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan="7" className="px-2 sm:px-6 py-4 text-center text-gray-500">
                                    Loading...
                                </td>
                            </tr>
                        ) : purchaseOrders.payload.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="px-2 sm:px-6 py-4 text-center text-gray-500">
                                    No purchase orders found.
                                </td>
                            </tr>
                        ) : (
                            purchaseOrders.payload.map((po) => (
                                <tr
                                    key={po._id}
                                    className="hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                                    onClick={() => openModal(po)}
                                >
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm font-semibold text-blue-600">
                                        {po.poNumber}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {moment(po.dateOfIssue).format("DD/MM/YYYY")}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {po.company?.name || "N/A"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm text-gray-900">
                                        {po.classificationCode || "N/A"}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm font-semibold text-green-600">
                                        {formatCurrency(po.totalAmount)}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                        {getStatusBadge(po.status)}
                                    </td>
                                    <td className="px-2 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-[11px] sm:text-sm">
                                        <div className="flex items-center gap-2">
                                            <FiEye
                                                className="pointer icon action-icon-view text-blue-500 hover:text-blue-700"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openModal(po);
                                                }}
                                                title="View Details"
                                            />
                                            <FiDownload
                                                className="pointer icon action-icon-view text-purple-500 hover:text-purple-700"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDownloadPurchaseOrder(po);
                                                }}
                                                title="Download Purchase Order"
                                            />
                                            {isPOEditable(po) && (
                                                <FiEdit
                                                    className="pointer icon action-icon-view text-orange-500 hover:text-orange-700"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openEditModal(po);
                                                    }}
                                                    title="Edit Purchase Order"
                                                />
                                            )}
                                            {(po.status === 'pending' || po.status === 'approved') && (
                                                <FiCheckCircle
                                                    className="pointer icon action-icon-view text-green-500 hover:text-green-700"
                                                    onClick={(e) => handleCompletePO(po, e)}
                                                    title="Mark as Completed"
                                                />
                                            )}
                                            {po.status !== 'completed' && po.status !== 'rejected' && (
                                                <FiXCircle
                                                    className="pointer icon action-icon-view text-red-500 hover:text-red-700"
                                                    onClick={(e) => handleRejectPO(po, e)}
                                                    title="Reject Purchase Order"
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                {purchaseOrders.payload.length > 0 && (
                    <div className="order-list-table-footer px-4 py-3 flex items-center justify-between border-t border-gray-200">
                        <div className="order-list-table-footer-inner flex-1 flex justify-between items-center">
                            <div className="order-list-table-footer-results text-sm text-gray-700">
                                Showing <span className="font-medium">{purchaseOrders.payload.length > 0 ? 1 : 0}</span> -{" "}
                                <span className="font-medium">{purchaseOrders.payload.length}</span> of{" "}
                                <span className="font-medium">{purchaseOrders.totalPages}</span> results
                            </div>
                            <div className="order-list-table-footer-pagination flex space-x-2">
                                <button
                                    onClick={() => setFilters({ ...filters, page: 1 })}
                                    disabled={filters.page === 1}
                                    title="First"
                                    aria-label="First page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        filters.page === 1
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;&lt;</span>
                                    <span className="order-list-pagination-text">First</span>
                                </button>
                                <button
                                    onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                                    disabled={filters.page === 1}
                                    title="Previous"
                                    aria-label="Previous page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        filters.page === 1
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&lt;</span>
                                    <span className="order-list-pagination-text">Previous</span>
                                </button>
                                <span className="order-list-pagination-page px-3 py-1 bg-gray-100 text-gray-900 rounded-md text-sm font-medium">
                                    Page {filters.page} of {Math.ceil(purchaseOrders.totalPages / filters.limit)}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                                    disabled={filters.page >= Math.ceil(purchaseOrders.totalPages / filters.limit)}
                                    title="Next"
                                    aria-label="Next page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        filters.page >= Math.ceil(purchaseOrders.totalPages / filters.limit)
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;</span>
                                    <span className="order-list-pagination-text">Next</span>
                                </button>
                                <button
                                    onClick={() => setFilters({ ...filters, page: Math.ceil(purchaseOrders.totalPages / filters.limit) })}
                                    disabled={filters.page >= Math.ceil(purchaseOrders.totalPages / filters.limit)}
                                    title="Last"
                                    aria-label="Last page"
                                    className={`order-list-pagination-btn px-3 py-1 rounded-md text-sm font-medium ${
                                        filters.page >= Math.ceil(purchaseOrders.totalPages / filters.limit)
                                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                            : "bg-orange-400 text-white hover:bg-orange-500"
                                    }`}
                                >
                                    <span className="order-list-pagination-icon" aria-hidden>&gt;&gt;</span>
                                    <span className="order-list-pagination-text">Last</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal for detailed view */}
            {showModal && selectedPO && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-20 sm:pt-4 pb-20 text-center sm:block sm:p-0">
                        {/* Background overlay */}
                        <div 
                            className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                            onClick={closeModal}
                        ></div>

                        {/* Modal panel - accounting for sidebar */}
                        <div 
                            className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full"
                            style={{
                                marginLeft: window.innerWidth >= 1367 ? `${sidebarWidth}px` : '0px'
                            }}
                        >
                            {/* Header */}
                            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-2xl font-bold text-white">
                                        {isEditMode ? "Edit Purchase Order" : "Purchase Order Details"}
                                    </h3>
                                    <p className="text-orange-100 text-sm mt-1">
                                        {selectedPO.poNumber}
                                    </p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <Close className="w-6 h-6" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="bg-white px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                                {isEditMode && editForm ? (
                                    <div className="space-y-6">
                                        {/* Editable Header Fields */}
                                        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                                            <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                                <FiPackage className="mr-2 text-orange-500" />
                                                Order Information
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        PO Number
                                                    </label>
                                                    <div className="w-full p-2 border border-gray-200 rounded-md bg-gray-100 text-gray-800">
                                                        {selectedPO.poNumber}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">Cannot be changed when editing.</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Classification Code
                                                    </label>
                                                    <div className="w-full p-2 border border-gray-200 rounded-md bg-gray-100 text-gray-800">
                                                        {selectedPO.classificationCode || "N/A"}
                                                    </div>
                                                    <p className="text-xs text-gray-500 mt-1">Cannot be changed when editing.</p>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Date of Issue *
                                                    </label>
                                                    <input
                                                        type="date"
                                                        value={editForm.dateOfIssue}
                                                        onChange={(e) =>
                                                            updateEditField("dateOfIssue", e.target.value)
                                                        }
                                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    />
                                                </div>
                                                <div className="min-w-0">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Company *
                                                    </label>
                                                    <Select
                                                        {...commonSelectProps}
                                                        options={companiesData.map((c) => ({
                                                            label: c.name,
                                                            value: String(c._id),
                                                        }))}
                                                        value={(() => {
                                                            const id =
                                                                editForm.company != null &&
                                                                editForm.company !== ""
                                                                    ? String(editForm.company)
                                                                    : "";
                                                            const row = companiesData.find(
                                                                (c) => String(c._id) === id
                                                            );
                                                            return row
                                                                ? {
                                                                      label: row.name,
                                                                      value: String(row._id),
                                                                  }
                                                                : null;
                                                        })()}
                                                        onChange={(selected) =>
                                                            updateEditField(
                                                                "company",
                                                                selected?.value ? String(selected.value) : ""
                                                            )
                                                        }
                                                        placeholder="Select Company"
                                                        isClearable
                                                        menuPortalTarget={
                                                            typeof document !== "undefined"
                                                                ? document.body
                                                                : null
                                                        }
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Remarks
                                                    </label>
                                                    <textarea
                                                        value={editForm.remarks}
                                                        onChange={(e) =>
                                                            updateEditField("remarks", e.target.value)
                                                        }
                                                        rows={3}
                                                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Editable Materials */}
                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                            <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4 flex justify-between items-center">
                                                <h4 className="text-lg font-bold text-white flex items-center">
                                                    <FiPackage className="mr-2" />
                                                    Materials ({editForm.materials.length})
                                                </h4>
                                                <button
                                                    type="button"
                                                    onClick={addBlankAddOnRow}
                                                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg"
                                                >
                                                    <FiPlus className="mr-1" />
                                                    Add Row
                                                </button>
                                            </div>
                                            <div className="overflow-x-auto max-h-[420px]">
                                                <table className="w-full min-w-max border-collapse">
                                                    <thead>
                                                        <tr className="bg-gray-50 border-b border-gray-200">
                                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Item Name</th>
                                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Item Code</th>
                                                            <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Price</th>
                                                            <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Qty</th>
                                                            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Measurements</th>
                                                            <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-700 w-12"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {editForm.materials.length === 0 ? (
                                                            <tr>
                                                                <td
                                                                    colSpan="6"
                                                                    className="px-3 py-6 text-center text-gray-500"
                                                                >
                                                                    No materials. Click "Add Row" to add one.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            editForm.materials.map((m, idx) => {
                                                                const hasItemCode = !!m.itemCode;
                                                                const matchedOption = itemCodeOptions.find(
                                                                    (opt) => opt.value === m.itemCode
                                                                );
                                                                const itemCodeValue = hasItemCode
                                                                    ? matchedOption || {
                                                                          label: m.itemCode,
                                                                          value: m.itemCode,
                                                                      }
                                                                    : null;
                                                                return (
                                                                <tr key={idx} className="hover:bg-gray-50">
                                                                    <td className="px-2 py-2 border-r border-gray-200">
                                                                        <input
                                                                            type="text"
                                                                            value={m.type || ""}
                                                                            disabled={hasItemCode}
                                                                            onChange={(e) =>
                                                                                updateMaterialField(
                                                                                    idx,
                                                                                    "type",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            title={
                                                                                hasItemCode
                                                                                    ? "Item Name is set by the selected Item Code. Clear the Item Code to edit."
                                                                                    : ""
                                                                            }
                                                                            placeholder={
                                                                                hasItemCode
                                                                                    ? ""
                                                                                    : "Type name (Add On)"
                                                                            }
                                                                            className={`w-44 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 outline-none ${
                                                                                hasItemCode
                                                                                    ? "bg-gray-100 text-gray-600 cursor-not-allowed"
                                                                                    : ""
                                                                            }`}
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 border-r border-gray-200">
                                                                        <div className="w-48">
                                                                            <Select
                                                                                {...commonSelectProps}
                                                                                options={itemCodeOptions}
                                                                                value={itemCodeValue}
                                                                                onChange={(selected) => {
                                                                                    const code =
                                                                                        selected?.value || "";
                                                                                    const mappedTitle =
                                                                                        materialsMapping[code];
                                                                                    if (code && mappedTitle) {
                                                                                        updateMaterialFields(idx, {
                                                                                            itemCode: code,
                                                                                            type: mappedTitle,
                                                                                            model: mappedTitle,
                                                                                        });
                                                                                    } else {
                                                                                        updateMaterialField(
                                                                                            idx,
                                                                                            "itemCode",
                                                                                            ""
                                                                                        );
                                                                                    }
                                                                                }}
                                                                                placeholder="Select Item Code"
                                                                                isClearable
                                                                                menuPortalTarget={
                                                                                    typeof document !== "undefined"
                                                                                        ? document.body
                                                                                        : null
                                                                                }
                                                                                menuPosition="fixed"
                                                                                menuPlacement="auto"
                                                                                maxMenuHeight={200}
                                                                                menuShouldScrollIntoView={false}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-2 py-2 border-r border-gray-200">
                                                                        <input
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            value={m.price ?? 0}
                                                                            onChange={(e) =>
                                                                                updateMaterialField(
                                                                                    idx,
                                                                                    "price",
                                                                                    parseFloat(e.target.value) || 0
                                                                                )
                                                                            }
                                                                            className="w-24 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 outline-none"
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 border-r border-gray-200">
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            step="1"
                                                                            value={m.quantity ?? 0}
                                                                            onChange={(e) =>
                                                                                updateMaterialField(
                                                                                    idx,
                                                                                    "quantity",
                                                                                    parseInt(e.target.value, 10) || 0
                                                                                )
                                                                            }
                                                                            className="w-20 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 outline-none"
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 border-r border-gray-200">
                                                                        <input
                                                                            type="text"
                                                                            value={m.measurements || ""}
                                                                            placeholder="e.g. 2m x 1.5m"
                                                                            onChange={(e) =>
                                                                                updateMaterialField(
                                                                                    idx,
                                                                                    "measurements",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                            className="w-36 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 outline-none"
                                                                        />
                                                                    </td>
                                                                    <td className="px-2 py-2 text-center">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => removeMaterialRow(idx)}
                                                                            title="Remove row"
                                                                            className="text-red-500 hover:text-red-700"
                                                                        >
                                                                            <FiTrash2 />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                                );
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                <>
                                {/* Info Cards Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                                        <div className="text-sm font-medium text-blue-700 mb-1">PO Number</div>
                                        <div className="text-lg font-bold text-blue-900">{selectedPO.poNumber}</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                                        <div className="text-sm font-medium text-green-700 mb-1">Total Amount</div>
                                        <div className="text-2xl font-bold text-green-900">{formatCurrency(selectedPO.totalAmount)}</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
                                        <div className="text-sm font-medium text-purple-700 mb-1">Date of Issue</div>
                                        <div className="text-lg font-semibold text-purple-900">{moment(selectedPO.dateOfIssue).format("DD MMM YYYY")}</div>
                                    </div>
                                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border border-indigo-200">
                                        <div className="text-sm font-medium text-indigo-700 mb-1">Status</div>
                                        <div className="mt-1">{getStatusBadge(selectedPO.status)}</div>
                                    </div>
                                </div>

                                {/* Details Section */}
                                <div className="bg-gray-50 rounded-xl p-6 mb-6 border border-gray-200">
                                    <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                        <FiPackage className="mr-2 text-orange-500" />
                                        Order Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <div className="text-sm font-medium text-gray-600 mb-1">Company</div>
                                            <div className="text-base font-semibold text-gray-900">{selectedPO.company?.name || "N/A"}</div>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-gray-600 mb-1">Classification Code</div>
                                            <div className="text-base font-semibold text-gray-900">{selectedPO.classificationCode || "N/A"}</div>
                                        </div>
                                        <div>
                                            <div className="text-sm font-medium text-gray-600 mb-1">Created At</div>
                                            <div className="text-base font-semibold text-gray-900">{moment(selectedPO.createdAt).format("DD MMM YYYY, HH:mm:ss")}</div>
                                        </div>
                                        {selectedPO.remarks && (
                                            <div className="md:col-span-2">
                                                <div className="text-sm font-medium text-gray-600 mb-1">Remarks</div>
                                                <div className="text-base text-gray-900 bg-white p-3 rounded-lg border border-gray-200">{selectedPO.remarks}</div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Materials List */}
                                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-4">
                                        <h4 className="text-lg font-bold text-white flex items-center">
                                            <FiPackage className="mr-2" />
                                            Materials ({selectedPO.materials?.length || 0})
                                        </h4>
                                    </div>
                                    <div className="overflow-x-auto max-h-96">
                                        <table className="w-full min-w-max border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 border-b border-gray-200">
                                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Item</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Code</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Class</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Company</th>
                                                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Price</th>
                                                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-700 border-r border-gray-200">Qty</th>
                                                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-700">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {selectedPO.materials && selectedPO.materials.length > 0 ? (
                                                    selectedPO.materials.map((material, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50 transition-colors duration-150">
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize border-r border-gray-200">{material.type || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{material.itemCode || material.code || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{material.classificationCode || 'N/A'}</td>
                                                            <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">
                                                                {material.company?.name || 
                                                                 companiesData.find(c => c._id === material.company)?.name || 
                                                                 'N/A'}
                                                            </td>
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right border-r border-gray-200">RM {(material.price || 0).toFixed(2)}</td>
                                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 text-center border-r border-gray-200">{material.quantity || material.amount || 0}</td>
                                                            <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                                                                RM {((material.price || 0) * (material.quantity || material.amount || 0)).toFixed(2)}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                                            No materials found
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                </>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-2 border-t border-gray-200">
                                {isEditMode ? (
                                    <>
                                        <button
                                            onClick={cancelEdit}
                                            disabled={isSavingEdit}
                                            className="px-6 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSaveEdit}
                                            disabled={isSavingEdit}
                                            className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                            {isSavingEdit ? "Saving…" : "Save Changes"}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        {isPOEditable(selectedPO) && (
                                            <button
                                                onClick={startEditFromModal}
                                                className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors duration-200 flex items-center"
                                            >
                                                <FiEdit className="mr-2" />
                                                Edit
                                            </button>
                                        )}
                                        <button
                                            onClick={closeModal}
                                            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors duration-200"
                                        >
                                            Close
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Complete Confirmation Modal */}
            {completeMenu && modalData && (
                <div className="overlayContainer">
                    <div className="center">
                        <div className="close" onClick={() => setCompleteMenu(false)}>
                            <Close />
                        </div>
                        <div className="actionContainer">
                            <div className="updateOrder">
                                <h3 className="updateProductTitle">
                                    Confirm Complete Purchase Order?
                                </h3>
                                <div className="flex flex-col items-center mb-4">
                                    <p className="text-gray-700 mb-2">
                                        Mark <strong>{modalData.poNumber}</strong> as completed?
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        Total Amount: {formatCurrency(modalData.totalAmount)}
                                    </p>
                                </div>
                                <div className="buttonContainer">
                                    <button
                                        className="confirm pointer green"
                                        onClick={confirmCompletePO}
                                    >
                                        Confirm
                                    </button>
                                    <button
                                        className="cancel pointer red"
                                        onClick={() => {
                                            setCompleteMenu(false);
                                            setModalData(null);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Confirmation Modal */}
            {rejectMenu && modalData && (
                <div className="overlayContainer">
                    <div className="center">
                        <div className="close" onClick={() => setRejectMenu(false)}>
                            <Close />
                        </div>
                        <div className="actionContainer">
                            <div className="updateOrder">
                                <h3 className="updateProductTitle">
                                    Reject Purchase Order
                                </h3>
                                <div className="flex flex-col items-center mb-4">
                                    <p className="text-gray-700 mb-2">
                                        Reject <strong>{modalData.poNumber}</strong>?
                                    </p>
                                    <p className="text-sm text-gray-500 mb-4">
                                        Total Amount: {formatCurrency(modalData.totalAmount)}
                                    </p>
                                    <div className="w-full">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Reason for Rejection (Optional)
                                        </label>
                                        <textarea
                                            className="w-full p-3 rounded-lg bg-cool-gray border border-gray-300 font-semibold text-lynch text-sm focus:outline-none focus:ring-2 focus:ring-buttonblue"
                                            rows="4"
                                            value={rejectRemarks}
                                            onChange={(e) => setRejectRemarks(e.target.value)}
                                            placeholder="Enter reason for rejection..."
                                        />
                                    </div>
                                </div>
                                <div className="buttonContainer">
                                    <button
                                        className="confirm pointer green"
                                        onClick={confirmRejectPO}
                                    >
                                        Confirm Rejection
                                    </button>
                                    <button
                                        className="cancel pointer red"
                                        onClick={() => {
                                            setRejectMenu(false);
                                            setModalData(null);
                                            setRejectRemarks("");
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurchaseOrders;

